import { ApiResult, HttpClient } from "../http.js";

/**
 * What the COMPANY may see about a recipient list. Deliberately no entries and no
 * token: the entries are the customer's residents' personal data and are admin-blind
 * by design, and the raw token exists only in the enable/regenerate response.
 */
export interface RecipientListStatusDto {
  enabled: boolean;
  recipientCount: number;
  tokenGeneratedAt?: string | null;
  lastUpdatedAt?: string | null;
}

export interface RecipientListTokenDto {
  listId: string;
  token: string;
}

export interface RecipientDto {
  id: string;
  name: string;
  addressUnit?: string | null;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CustomerRecipientListDto {
  projectAddress: string;
  companyName: string;
  recipients: RecipientDto[];
}

export interface AddRecipientPayload {
  name: string;
  addressUnit?: string | null;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
}

/**
 * Partial update. Omit a field to leave it unchanged; set the matching `clear*` flag
 * to blank it — JSON cannot distinguish "absent" from "explicitly null".
 */
export interface UpdateRecipientPayload {
  name?: string | null;
  addressUnit?: string | null;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  clearAddressUnit?: boolean;
  clearPhone?: boolean;
  clearEmail?: boolean;
  clearNotes?: boolean;
}

/**
 * Recipient lists — replaces the Firebase `recipientLists` callables, which had become
 * unreachable in production (they required a Firebase auth session the app no longer
 * has, and resolved projects from Firestore).
 *
 * The company-facing calls are session-authenticated; the `byToken` calls are anonymous
 * and authorised purely by possession of the customer link.
 */
export class RecipientListsApi {
  constructor(private readonly http: HttpClient) {}

  status(projectId: string): Promise<ApiResult<RecipientListStatusDto | null>> {
    return this.http.get<RecipientListStatusDto | null>(
      `/api/projects/${encodeURIComponent(projectId)}/recipient-list`,
    );
  }

  enable(projectId: string): Promise<ApiResult<RecipientListTokenDto>> {
    return this.http.post<RecipientListTokenDto>(
      `/api/projects/${encodeURIComponent(projectId)}/recipient-list/enable`,
    );
  }

  disable(projectId: string): Promise<ApiResult<boolean>> {
    return this.http.post<boolean>(
      `/api/projects/${encodeURIComponent(projectId)}/recipient-list/disable`,
    );
  }

  regenerateToken(projectId: string): Promise<ApiResult<RecipientListTokenDto>> {
    return this.http.post<RecipientListTokenDto>(
      `/api/projects/${encodeURIComponent(projectId)}/recipient-list/regenerate-token`,
    );
  }

  getByToken(token: string): Promise<ApiResult<CustomerRecipientListDto>> {
    return this.http.get<CustomerRecipientListDto>("/api/recipient-lists/by-token", {
      query: { token },
    });
  }

  addRecipient(token: string, payload: AddRecipientPayload): Promise<ApiResult<RecipientDto>> {
    return this.http.post<RecipientDto>("/api/recipient-lists/by-token/recipients", {
      query: { token },
      body: payload,
    });
  }

  updateRecipient(
    token: string,
    recipientId: string,
    payload: UpdateRecipientPayload,
  ): Promise<ApiResult<RecipientDto>> {
    return this.http.put<RecipientDto>(
      `/api/recipient-lists/by-token/recipients/${encodeURIComponent(recipientId)}`,
      { query: { token }, body: payload },
    );
  }

  deleteRecipient(token: string, recipientId: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(
      `/api/recipient-lists/by-token/recipients/${encodeURIComponent(recipientId)}`,
      { query: { token } },
    );
  }
}
